import { useContext } from 'react'
import { DateTime } from 'luxon'

import { useForm, useLatestEntity } from '@domain-group/react-hooks'
import type { UseFormOptions } from '@domain-group/react-hooks'

import { digObject, launchModal } from '@domain-group/javascript-utils'

import useDispatch from '@hooks/useDispatch'
import useSelector from '@hooks/useSelector'

import * as campaignPortalActions from '@redux/modules/campaignPortal'

import defaultFormState, { requiredFields } from '@models/campaignPortal'

import PageContext from '@contexts/pageContext'

import type { AppDispatch } from '@redux/store'
import type { DeleteParams } from '@redux/modules/types'

import type { CampaignModel, CampaignPortalModel, CampaignPortalRequestOptions } from '@models/types'

export type GenerateUrlsOptions = {
  prefixRoute?: string,
  queryString?: string,
}

export const generateUrls = (campaign?: Partial<CampaignModel>, options?: GenerateUrlsOptions) => {
  const { prefixRoute = 'systemManager', queryString = '' } = options || {}

  return {
    editPortalsUrl: `#/${prefixRoute}/portals${queryString}`,
  }
}

type CreateCampaignPortalParams = {
  campaignPortalParams: Partial<CampaignPortalModel>,
  dispatch: AppDispatch,
  requestOptions?: CampaignPortalRequestOptions,
}

const createCampaignPortal = (params: CreateCampaignPortalParams) => {
  const { dispatch, campaignPortalParams, requestOptions } = params
  const { createCampaignPortal: createFn } = campaignPortalActions

  return dispatch(createFn(campaignPortalParams, requestOptions))
}

type UpdateCampaignPortalParams = {
  campaignPortal: CampaignPortalModel,
  campaignPortalParams: Partial<CampaignPortalModel>,
  dispatch: AppDispatch,
  requestOptions?: CampaignPortalRequestOptions,
}

const updateCampaignPortal = (params: UpdateCampaignPortalParams) => {
  const {
    dispatch, campaignPortal, campaignPortalParams, requestOptions,
  } = params
  const { updateCampaignPortal: updateFn } = campaignPortalActions

  const updatedParams = {
    id: campaignPortal.id,
    ...campaignPortalParams,
  }

  return dispatch(updateFn(updatedParams, requestOptions))
}

type DeleteCampaignPortalParams = {
  campaignPortal: DeleteParams<CampaignPortalModel>,
  dispatch: AppDispatch,
}

const deleteCampaignPortal = (params: DeleteCampaignPortalParams) => {
  const { dispatch, campaignPortal } = params
  const { deleteCampaignPortal: deleteFn } = campaignPortalActions

  return dispatch(deleteFn(campaignPortal))
}

type CustomFormOptions = {
  customRequiredFields?: UseFormOptions['requiredFields'],
}

export function useCampaignPortalForm(
  campaignPortal: Partial<CampaignPortalModel>,
  options: UseFormOptions & CustomFormOptions = {},
) {
  const { customRequiredFields = [], validateOn } = options || {}

  const campaignPortalForm = useForm(
    defaultFormState,
    { entity: campaignPortal, requiredFields: [...requiredFields, ...customRequiredFields], validateOn },
    [campaignPortal.id, campaignPortal.cache_key],
  )

  return {
    ...campaignPortalForm,
  }
}

export const useRelations = (campaignPortal: Partial<CampaignPortalModel> = {}) => {
  const { campaign_id, portal_id } = campaignPortal

  const { agencies, campaigns, portals } = useSelector(reduxState => reduxState.entities)

  const campaign = campaign_id && campaigns[campaign_id] ? campaigns[campaign_id] : {}

  // @ts-expect-error TS2339
  const { agency_id } = campaign
  const agency = agency_id && agencies[agency_id] ? agencies[agency_id] : {}

  const portal = portal_id && portals[portal_id] ? portals[portal_id] : {}

  return {
    agency,
    campaign,
    portal,
  }
}

type Options = {
  generateUrlsOptions?: GenerateUrlsOptions,
}

function useCampaignPortal(initEntity: Partial<CampaignPortalModel> = {}, options: Options = {}) {
  // @ts-expect-error TS2322
  const { entity: campaignPortal }: { entity: CampaignPortalModel } = useLatestEntity(initEntity, 'campaignPortals')

  const { generateUrlsOptions } = options

  const dispatch = useDispatch()

  const { callbacks } = useContext(PageContext)

  const { agency, campaign, portal } = useRelations(campaignPortal)

  const {
    creating, deleting, loadedIds, loading, updating,
  } = useSelector(reduxState => reduxState.campaignPortals)

  const lastSyncDate = digObject(campaignPortal, 'dates.sync.date_time')
  const lastSyncDateObj = lastSyncDate ? DateTime.fromFormat(lastSyncDate, 'dd/MM/yyyy HH:mm') : null

  return {
    agency,
    campaign,
    callbacks: {
      createCampaignPortal: (
        campaignPortalParams: Partial<CampaignPortalModel>,
        entityOptions?: CampaignPortalRequestOptions,
      ) => (
        createCampaignPortal({ campaignPortalParams, dispatch, requestOptions: entityOptions })
      ),
      createOrEditCampaignPortal: () => launchModal({
        callbacks,
        modalKey: 'CreateOrEditCampaignPortalModal',
        payload: { campaignPortal },
      }),
      deleteCampaignPortal: () => deleteCampaignPortal({ dispatch, campaignPortal }),
      editCampaignPortal: () => launchModal({
        callbacks,
        modalKey: 'EditCampaignPortalModal',
        payload: { campaignPortal },
      }),
      updateCampaignPortal: (
        campaignPortalParams: Partial<CampaignPortalModel>,
        entityOptions?: CampaignPortalRequestOptions,
      ) => (
        updateCampaignPortal({
          campaignPortal, campaignPortalParams, dispatch, requestOptions: entityOptions,
        })
      ),
    },
    campaignPortal,
    creating,
    deleting,
    lastSyncDateObj,
    loading: loading && !loadedIds.includes(campaignPortal.id),
    portal,
    updating,
    urls: generateUrls(campaign, generateUrlsOptions),
  }
}

export default useCampaignPortal
